import { useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const API_URL = '';

/**
 * useChallengeActivity Hook
 * 
 * Tracks activity events for the March Madness Challenge system.
 * Events are sent to backend to calculate active minutes and reviews.
 * 
 * Activity is only tracked for reviewers (session owners).
 */
export const useChallengeActivity = (sessionId, isReviewer) => {
  const lastEventRef = useRef(null);
  const throttleMs = 5000; // Minimum 5 seconds between same event types
  
  /**
   * Track an activity event
   * @param {string} eventType - Type of event (play, pause, next, etc.)
   * @param {string} eventCategory - Category (playlist or submission)
   * @param {object} metadata - Additional event metadata
   */
  const trackActivity = useCallback(async (eventType, eventCategory, metadata = {}) => {
    // Only track for reviewers with active sessions
    if (!isReviewer || !sessionId) {
      return;
    }
    
    // Throttle same event types
    const eventKey = `${eventType}-${eventCategory}`;
    const now = Date.now();
    
    if (lastEventRef.current && 
        lastEventRef.current.key === eventKey && 
        now - lastEventRef.current.time < throttleMs) {
      return; // Skip - too soon for same event
    }
    
    lastEventRef.current = { key: eventKey, time: now };
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      await axios.post(
        `${API_URL}/api/challenges/activity`,
        {
          session_id: sessionId,
          event_type: eventType,
          event_category: eventCategory,
          metadata
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log(`🏆 Challenge activity tracked: ${eventCategory}/${eventType}`);
    } catch (error) {
      // Silent fail - don't interrupt user experience
      console.debug('Challenge activity tracking error:', error.message);
    }
  }, [sessionId, isReviewer]);
  
  // Convenience methods for common events
  const trackPlaylistEvent = useCallback((eventType, metadata = {}) => {
    trackActivity(eventType, 'playlist', metadata);
  }, [trackActivity]);
  
  const trackSubmissionEvent = useCallback((eventType, metadata = {}) => {
    trackActivity(eventType, 'submission', metadata);
  }, [trackActivity]);
  
  // Track session start when hook initializes (for reviewer)
  useEffect(() => {
    if (isReviewer && sessionId) {
      const startChallengeSession = async () => {
        try {
          const token = localStorage.getItem('access_token');
          if (!token) return;
          
          await axios.post(
            `${API_URL}/api/challenges/session/start?session_id=${sessionId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('🏆 Challenge session started');
        } catch (error) {
          console.debug('Challenge session start error:', error.message);
        }
      };
      
      startChallengeSession();
    }
  }, [isReviewer, sessionId]);
  
  return {
    trackActivity,
    trackPlaylistEvent,
    trackSubmissionEvent,
    
    // Specific event trackers
    trackPlay: (trackId) => trackPlaylistEvent('play', { trackId }),
    trackPause: () => trackPlaylistEvent('pause'),
    trackNext: () => trackPlaylistEvent('next'),
    trackPrevious: () => trackPlaylistEvent('previous'),
    trackSeek: (position) => trackPlaylistEvent('seek', { position }),
    trackTrackChange: (trackId, trackTitle) => trackPlaylistEvent('track_change', { trackId, trackTitle }),
    
    trackSubmissionReceived: (submissionId) => trackSubmissionEvent('received', { submissionId }),
    trackSubmissionOpened: (submissionId) => trackSubmissionEvent('opened', { submissionId }),
    trackSubmissionPlayed: (submissionId) => trackSubmissionEvent('played', { submissionId }),
    trackReviewCompleted: (submissionId, action) => trackSubmissionEvent('review_completed', { submissionId, action })
  };
};

export default useChallengeActivity;
